import { ReactNode } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { ActivityOfType } from '@/components/activities/Activity';
import AlertBar from '@/design_system/AlertBar';
import Button from '@/design_system/Button/Button';
import Tooltip from '@/design_system/Tooltip';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconInfo from '@/icons/Info.svg';
import { JOB_REFUSED_REASONS } from '@/models/article';
import { computeDuplicateDefectNumber, useGetDefectName } from '@/models/defectType';
import { WorkshopFromGetMany } from '@/models/workshop';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { createBEMClasses } from '@/utils/classname';

const { element } = createBEMClasses('workshop-list');

const EmphasizedStatusText = ({ children }: { children: ReactNode }) => (
  <InlineText weight="medium" size="200" color="primary">
    {children}
  </InlineText>
);

const WorkshopStatusLineText = ({
  showAlreadyWorked,
  showRefused,
  showDefectsWarning,
}: {
  showAlreadyWorked: boolean;
  showRefused: boolean;
  showDefectsWarning: boolean;
}) => {
  if (showAlreadyWorked && showRefused && showDefectsWarning) {
    return (
      <Trans id="components.workshop-list.status.worked-refused-defects">
        This workshop <EmphasizedStatusText>worked on this item</EmphasizedStatusText>,{' '}
        <EmphasizedStatusText>refused the job</EmphasizedStatusText> and{' '}
        <EmphasizedStatusText>can't handle all defects</EmphasizedStatusText>.
      </Trans>
    );
  }

  if (showAlreadyWorked && showRefused) {
    return (
      <Trans id="components.workshop-list.status.worked-refused">
        This workshop <EmphasizedStatusText>worked on this item</EmphasizedStatusText> and{' '}
        <EmphasizedStatusText>refused the job</EmphasizedStatusText>.
      </Trans>
    );
  }

  if (showAlreadyWorked && showDefectsWarning) {
    return (
      <Trans id="components.workshop-list.status.worked-defects">
        This workshop <EmphasizedStatusText>worked on this item</EmphasizedStatusText> and{' '}
        <EmphasizedStatusText>can't handle all defects</EmphasizedStatusText>.
      </Trans>
    );
  }

  if (showRefused && showDefectsWarning) {
    return (
      <Trans id="components.workshop-list.status.refused-defects">
        This workshop <EmphasizedStatusText>refused the job</EmphasizedStatusText> and{' '}
        <EmphasizedStatusText>can't handle all defects</EmphasizedStatusText>.
      </Trans>
    );
  }

  if (showAlreadyWorked) {
    return (
      <Trans id="components.workshop-list.status.worked">
        This workshop <EmphasizedStatusText>worked on this item</EmphasizedStatusText>.
      </Trans>
    );
  }

  if (showRefused) {
    return (
      <Trans id="components.workshop-list.status.refused">
        This workshop <EmphasizedStatusText>refused the job</EmphasizedStatusText>.
      </Trans>
    );
  }

  return (
    <Trans id="components.workshop-list.status.defects">
      This workshop <EmphasizedStatusText>can't handle all defects</EmphasizedStatusText>.
    </Trans>
  );
};

export const WorkshopListItemWarningsBanner = ({
  workshop,
  jobRefusedActivity,
  isDisabled,
}: {
  workshop: WorkshopFromGetMany;
  jobRefusedActivity?: ActivityOfType<'job_refused'>;
  isDisabled?: boolean;
}) => {
  const { article } = useArticleContext();
  const { i18n } = useLingui();
  const getDefectName = useGetDefectName();

  const defectsNotHandledByTheWorkshop = workshop.defectsNotHandledByTheWorkshop;
  const showCustomsWarning = !!workshop.areCustomsExpected;
  const showDefectsWarning = !!defectsNotHandledByTheWorkshop?.length;
  const showAlreadyWorked =
    !isDisabled &&
    article.snapshot.articleActions.some((action) => action.doneByWorkshop?.id === workshop.id);
  const showRefused = !!jobRefusedActivity;
  const showStatusWarning = showDefectsWarning || showAlreadyWorked || showRefused;

  if (!showCustomsWarning && !showStatusWarning) {
    return null;
  }

  const articleDefects = article.snapshot.articleDefects;
  const defectNames =
    showDefectsWarning && defectsNotHandledByTheWorkshop
      ? articleDefects
          .filter((defect) => defectsNotHandledByTheWorkshop.find(({ id }) => id === defect.id))
          .map((defect) => {
            const name = getDefectName(defect);
            const duplicateNumber = computeDuplicateDefectNumber(defect, articleDefects);

            return `${name} ${articleDefects.length > 1 && duplicateNumber ? ` #${duplicateNumber}` : ''}`;
          })
      : [];
  const defectNamesText = defectNames.join(', ');

  const refusedReasonDetail = jobRefusedActivity
    ? JOB_REFUSED_REASONS.find(({ id }) => id === jobRefusedActivity.data.reason)
    : undefined;
  const refusedReason =
    !refusedReasonDetail || refusedReasonDetail.id === 'custom'
      ? undefined
      : i18n._(
          jobRefusedActivity?.data.onBehalfOfWorkshop
            ? refusedReasonDetail.onBehalfLabel
            : refusedReasonDetail.label
        );
  const refusedComment = jobRefusedActivity?.data.comment;
  const refusalReasonText = [refusedReason, refusedComment ? `"${refusedComment}"` : undefined]
    .filter(Boolean)
    .join(' - ');

  const showRefusedTooltip = showRefused && !!refusalReasonText;
  const showStatusTooltip = showDefectsWarning || showRefusedTooltip;

  return (
    <AlertBar type="warning" className={element('item__warnings-banner')} size="small">
      <div>
        {showStatusWarning && (
          <Paragraph size="200" color="secondary">
            •{' '}
            <WorkshopStatusLineText
              showAlreadyWorked={showAlreadyWorked}
              showRefused={showRefused}
              showDefectsWarning={showDefectsWarning}
            />
            {showStatusTooltip && (
              <>
                {' '}
                <Tooltip
                  textAlign="start"
                  content={
                    <ul>
                      {showRefusedTooltip && (
                        <li>
                          <Trans id="components.workshop-list.status.tooltip.refusal-reason">
                            Refusal reason: {refusalReasonText}
                          </Trans>
                        </li>
                      )}
                      {showDefectsWarning && (
                        <li>
                          <Trans id="components.workshop-list.status.tooltip.defects-not-handled">
                            Defects not handled: {defectNamesText}
                          </Trans>
                        </li>
                      )}
                    </ul>
                  }
                >
                  <Button
                    variant="style-less"
                    className="text-secondary"
                    style={{ verticalAlign: 'top' }}
                  >
                    <IconInfo style={{ fontSize: '1rem' }} />
                  </Button>
                </Tooltip>
              </>
            )}
          </Paragraph>
        )}

        {showCustomsWarning && (
          <Paragraph size="200" color="secondary">
            •{' '}
            <Trans id="components.workshop-list.customs-expected">
              <EmphasizedStatusText>Customs information</EmphasizedStatusText> required
            </Trans>
          </Paragraph>
        )}
      </div>
    </AlertBar>
  );
};
